/* Source: /home/sidsolaiyappan/skooch_ai/shared/onboarding_handlers.jac */
import {__jacJsx, __jacSpawn} from "@jac/runtime";
import { get_supabase } from "./supabase_config.js";
import { getAccessToken } from "./auth_token.js";
import { loadQuizFromStorage, _persistQuizProgress, _clearQuizProgress } from "./quiz_persistence.js";
import { _buildProfileRow } from "./profile_save.js";
import { syncSchPrefs } from "./profile_handlers.js";
import { local_compute_energy_data, local_serialize_tiers } from "./profile_serialization.js";
import { computeEnergyDataForState } from "./energy_curve.js";
import { _exitSandboxForAuth } from "./sandbox_lifecycle.js";
import { _listFixedEventsExpanded } from "./ical_handlers.js";
import { goToScheduler } from "./nav_handlers.js";
import { runRandomize } from "../scheduler/sandbox_scenarios.js";
import { getStoreOnboardingStep, getStoreViewMode, getStoreIsLoggedIn, getStoreIsAnonymous, getStoreUserId, getStorePrefFocusPeak, getStorePrefWorkStyle, getStoreSleepImportance, getStoreWorkLifeBalance, getStoreBufferMins, getStoreThemeDark, getStoreCategoryTiers, getStoreEnergyShape, setStoreOnboardingStep, setStorePrefWorkStyle, setStorePrefFocusPeak, setStoreSleepImportance, setStoreWorkLifeBalance, setStoreBufferMins, setStoreThemeDark, setStoreTasks, setStoreFixedBlocks, setStoreViewMode, setStoreProfileSkipped, setStoreEnergyData, setStoreEnergyShape, setStoreChatLimit, setStoreUserId, setStoreIsLoggedIn, setStoreIsAnonymous, setStoreAccountClaimed, setStoreCurrentPage, setStoreProfileLoaded, setStoreChatMessagesUsedToday, setStoreAuthMode, setStoreSaveErrorNotice } from "./store.js";
function nextOnboardingStep() {
  window.__skoochOnbStep = getStoreOnboardingStep();
  if (!window.__skoochOnbPopstateBound) {
    window.__skoochOnbPopstateBound = true;
    window.addEventListener("popstate", e => {
      if ((getStoreViewMode() !== "quiz")) {
        return;
      }
      let _cur = window.__skoochOnbStep;
      if ((_cur > 0)) {
        let _back = (_cur - 1);
        window.__skoochOnbStep = _back;
        setStoreOnboardingStep(_back);
        _persistQuizProgress({"onboardingStep": _back});
      }
    });
  }
  let _ns = getStoreOnboardingStep();
  if ((getStoreOnboardingStep() < 4)) {
    let _next = (getStoreOnboardingStep() + 1);
    setStoreOnboardingStep(_next);
    window.__skoochOnbStep = _next;
    _ns = _next;
    history.pushState({"skoochOnbStep": _next}, "", window.location.pathname);
  }
  _persistQuizProgress({"onboardingStep": _ns});
}
function prevOnboardingStep() {
  if ((getStoreOnboardingStep() > 0)) {
    if (window.__skoochOnbPopstateBound) {
      history.back();
    } else {
      let _prev = (getStoreOnboardingStep() - 1);
      setStoreOnboardingStep(_prev);
      window.__skoochOnbStep = _prev;
      _persistQuizProgress({"onboardingStep": _prev});
    }
  }
}
function _daypartForShape(shape) {
  if ((shape === "lark")) {
    return "morning";
  }
  if ((shape === "midday")) {
    return "midday";
  }
  if ((shape === "two_peaks")) {
    return "morning";
  }
  if ((shape === "owl")) {
    return "evening";
  }
  return "midday";
}
function onSetOnboardingPref(field, value) {
  if ((field === "prefWorkStyle")) {
    setStorePrefWorkStyle(value);
  } else if ((field === "prefFocusPeak")) {
    setStorePrefFocusPeak(value);
  } else if ((field === "prefEnergyShape")) {
    setStoreEnergyShape(value);
    setStorePrefFocusPeak(_daypartForShape(value));
    try {
      window.localStorage.setItem("skooch_energy_shape", String(value));
    } catch (__jac_e) {
      if (true) {
        let _ehls = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  } else if ((field === "sleepImportance")) {
    setStoreSleepImportance(value);
  } else if ((field === "workLifeBalance")) {
    setStoreWorkLifeBalance(value);
  } else if ((field === "bufferMins")) {
    setStoreBufferMins(value);
  } else if ((field === "themeDark")) {
    setStoreThemeDark(value);
  }
  let _ov = {};
  _ov[field] = value;
  _persistQuizProgress(_ov);
}
async function finishOnboarding() {
  _clearQuizProgress();
  if (getStoreIsLoggedIn()) {
    if (getStoreIsAnonymous()) {
      await _anonSaveCompletedProfile(getStoreUserId());
      setStoreTasks([]);
      window.__skoochSyncSchTasksFromPending();
    } else {
      let _accessToken = await getAccessToken();
      let _saveRes = await window.__skoochSaveProfileFromState(getStoreUserId(), _accessToken);
      if ((_saveRes && _saveRes["_sb_error"])) {
        console.log("finishOnboarding profile save failed:", _saveRes);
        setStoreSaveErrorNotice("Your survey answers couldn't be saved to the cloud. They're still active for this session \u2014 check your connection, then hit Save on the Profile page to retry.");
      }
      localStorage.removeItem("skooch_quiz");
      syncSchPrefs();
      setStoreEnergyData(computeEnergyDataForState(getStorePrefFocusPeak(), getStoreSleepImportance(), getStoreEnergyShape(), local_compute_energy_data(getStorePrefFocusPeak(), getStoreSleepImportance())));
      try {
        let _onbTasks = [];
        if (window.__skoochServerless) {
          let _onbTCols = "id,title,category,duration_mins,priority,deadline,status,completed,is_recurring,recurrence_interval,recurrence_unit,recurrence_weekday";
          let _onbTResp = await get_supabase().from("tasks").select(_onbTCols).eq("user_id", getStoreUserId()).order("created_at", {"ascending": true}).order("id", {"ascending": true});
          _onbTasks = ((_onbTResp && _onbTResp.data) ? _onbTResp.data : []);
        } else {
          _onbTasks = await window.__skoochJacCall("list_tasks", {"user_id": getStoreUserId(), "access_token": _accessToken});
        }
        setStoreTasks(_onbTasks);
        window.__skoochSyncSchTasksFromPending();
        setStoreFixedBlocks(await _listFixedEventsExpanded(getStoreUserId(), _accessToken));
      } catch (__jac_e) {
        if (true) {
          let _onbHydErr = __jac_e;
          console.log("post-onboarding hydration failed (non-fatal):", _onbHydErr);
        } else {
          throw __jac_e;
        }
      }
    }
    setStoreViewMode("app");
    setStoreProfileSkipped(false);
    window.__skoochSetProfileComplete(true);
    window.__skoochProfileComplete = true;
    window.__skoochFirstRunSandbox = true;
    await runRandomize();
    window.__skoochSetCoachStep(0);
    localStorage.setItem("skooch_coach_dismissed", "1");
    await goToScheduler();
    localStorage.setItem("skooch_active_tab", "sandbox");
  } else {
    let quizOrder = local_serialize_tiers(getStoreCategoryTiers());
    localStorage.setItem("skooch_quiz", ((((((((((((getStorePrefFocusPeak() + "|") + getStorePrefWorkStyle()) + "|") + String(getStoreBufferMins())) + "|") + (getStoreThemeDark() ? "1" : "0")) + "|") + String(getStoreSleepImportance())) + "|") + String(getStoreWorkLifeBalance())) + "|") + quizOrder));
    window.__skoochSetQuizDone(true);
    setStoreViewMode("auth");
    setStoreAuthMode("signup");
  }
}
async function _anonSaveCompletedProfile(uid) {
  try {
    let _sbU = get_supabase();
    let _upResp = await _sbU.from("profiles").upsert(_buildProfileRow(uid, true, window.__skoochCurrentPrefsSnapshot()), {"onConflict": "user_id"});
    if ((_upResp && _upResp.error)) {
      console.log("anon completed-profile upsert rejected:", _upResp.error);
      setStoreSaveErrorNotice("Your survey answers couldn't be saved. They're still active for this session \u2014 check your connection, then hit Save on the Profile page to retry.");
    }
  } catch (__jac_e) {
    if (true) {
      let _saveErr = __jac_e;
      console.log("anon completed-profile upsert failed:", _saveErr);
      setStoreSaveErrorNotice("Your survey answers couldn't be saved. They're still active for this session \u2014 check your connection, then hit Save on the Profile page to retry.");
    } else {
      throw __jac_e;
    }
  }
  localStorage.removeItem("skooch_quiz");
  _clearQuizProgress();
  syncSchPrefs();
  setStoreEnergyData(computeEnergyDataForState(getStorePrefFocusPeak(), getStoreSleepImportance(), getStoreEnergyShape(), local_compute_energy_data(getStorePrefFocusPeak(), getStoreSleepImportance())));
  setStoreChatLimit(25);
}
async function skipOnboarding() {
  try {
    let sb = get_supabase();
    let _result = await sb.auth.signInAnonymously();
    if (_result.error) {
      console.log("anon sign-in failed:", _result.error);
      return;
    }
    let _uid = _result.data.user.id;
    let _tok = _result.data.session.access_token;
    setStoreUserId(_uid);
    let _accessToken = _tok;
    setStoreIsLoggedIn(true);
    setStoreIsAnonymous(true);
    setStoreAccountClaimed(false);
    window.__skoochIsAnon = true;
    let _hadQuiz = loadQuizFromStorage();
    if (_hadQuiz) {
      await _anonSaveCompletedProfile(_uid);
      setStoreProfileSkipped(false);
      window.__skoochSetProfileComplete(true);
      window.__skoochProfileComplete = true;
      setStoreProfileLoaded(true);
    } else {
      try {
        await sb.from("profiles").upsert({"user_id": _uid, "onboarding_completed": false, "onboarding_skipped": true}, {"onConflict": "user_id"});
      } catch (__jac_e) {
        if (true) {
          let e2 = __jac_e;
          console.log("profile skip upsert failed (non-fatal):", e2);
        } else {
          throw __jac_e;
        }
      }
      setStoreProfileSkipped(true);
      window.__skoochSetProfileComplete(false);
      window.__skoochProfileComplete = false;
      setStoreProfileLoaded(true);
      setStoreChatLimit(10);
    }
    setStoreViewMode("app");
    setStoreCurrentPage("dashboard");
    setStoreOnboardingStep(0);
    _clearQuizProgress();
    setStoreChatMessagesUsedToday(0);
    _exitSandboxForAuth();
    localStorage.setItem("skooch_active_tab", "dashboard");
  } catch (__jac_e) {
    if (true) {
      let e = __jac_e;
      console.log("skipOnboarding error:", e);
    } else {
      throw __jac_e;
    }
  }
}
export {_anonSaveCompletedProfile, _daypartForShape, finishOnboarding, nextOnboardingStep, onSetOnboardingPref, prevOnboardingStep, skipOnboarding};
if (typeof globalThis !== "undefined") { if (!globalThis.__jacEndpointEffects__) globalThis.__jacEndpointEffects__ = {}; Object.assign(globalThis.__jacEndpointEffects__, {"func:nextOnboardingStep": {"is_writer": true, "touches": ["*"]}, "func:prevOnboardingStep": {"is_writer": true, "touches": ["*"]}, "func:_daypartForShape": {"is_writer": false, "touches": ["*"]}, "func:onSetOnboardingPref": {"is_writer": false, "touches": ["*"]}, "func:finishOnboarding": {"is_writer": true, "touches": ["*"]}, "func:_anonSaveCompletedProfile": {"is_writer": false, "touches": ["*"]}, "func:skipOnboarding": {"is_writer": true, "touches": ["*"]}}); };
//# sourceMappingURL=onboarding_handlers.js.map
